import { Accordion, AccordionVariants } from '../Accordion/Accordion';
import { useCallback } from 'react';
import { Color, Divider, Space } from '@gousto-internal/citrus-react';
import { DietaryFiltersFieldset, ResetFilterButton } from './styles';
import {
  DIETARY_FILTERS_ACCORDION_LABEL,
  DietaryFiltersAccordionTitle,
} from './DietaryFiltersAccordionTitle';
import { DietaryFilter } from './DietaryFilter';
import { queueTrackingEvent } from '../../event-tracking/queueTrackingEvent';
import { TRACKING_EVENT_NAMES } from '../../event-tracking/events';

export type DietaryCollection = { name: string; slug: string; recipeCount: number };

type MenuDietaryFiltersProps = {
  onReset: () => void;
  onToggleDietaryCollection: (slug: string) => void;
  dietaryCollections: DietaryCollection[];
  dietaryCollectionsState: Map<string, boolean>;
};

export const MenuDietaryFilters = (props: MenuDietaryFiltersProps) => {
  const { dietaryCollections, dietaryCollectionsState, onToggleDietaryCollection, onReset } = props;

  const handleReset = useCallback(() => {
    queueTrackingEvent(TRACKING_EVENT_NAMES.MENU_DIETARY_FILTERS_RESET, {
      filter_type: 'dietary',
    });
    onReset();
  }, [onReset]);

  const handleCollectionChange = useCallback(
    (slug: string, name: string, isCurrentlyChecked: boolean) => {
      return function handleCollectionClick() {
        queueTrackingEvent(TRACKING_EVENT_NAMES.MENU_DIETARY_FILTER_TOGGLE, {
          filter_type: 'dietary',
          filter_slug: slug,
          filter_name: name,
          is_selected: !isCurrentlyChecked,
        });
        onToggleDietaryCollection(slug);
      };
    },
    [onToggleDietaryCollection],
  );

  if (dietaryCollections.length === 0) {
    return null;
  }

  return (
    <>
      <Accordion
        id="dietary-collection-filters"
        variant={AccordionVariants.DIETARY_FILTERS}
        initialIsExpanded={true}
        title={<DietaryFiltersAccordionTitle />}
      >
        <DietaryFiltersFieldset aria-labelledby={DIETARY_FILTERS_ACCORDION_LABEL}>
          {dietaryCollections.map((dietaryCollection) => {
            const isChecked = dietaryCollectionsState.get(dietaryCollection.slug);

            return (
              <DietaryFilter
                key={dietaryCollection.slug}
                dietaryCollection={dietaryCollection}
                onChange={handleCollectionChange(
                  dietaryCollection.slug,
                  dietaryCollection.name,
                  isChecked ?? false,
                )}
                isChecked={isChecked}
              />
            );
          })}

          <ResetFilterButton onClick={handleReset}>Reset all filters</ResetFilterButton>
        </DietaryFiltersFieldset>

        <Space size={3} />
      </Accordion>

      <Divider bg={Color.NeutralGrey_100} />
    </>
  );
};
